import { ReactElement, ReactNode } from 'react';
import {
  MdCheck,
  MdDeleteOutline,
  MdIosShare,
  MdOutlineFileDownload,
  MdOutlineGrade,
  MdOutlinePushPin,
  MdOutlineVisibilityOff
} from 'react-icons/md';
import { RiMore2Fill } from 'react-icons/ri';
import { ActivityKind, AnyActivity, Event } from '@blisspointmedia/bpm-types/dist/OracleActivity';
import { Dropdown, DropdownToggleType, MenuItem } from '@Components/Dropdown';
import { Placement, StretchMode } from '@Components/designConstants';
import {
  ACTIONS_MENU_CLASS,
  ACTIONS_MENU_DESTRUCTIVE_CLASS,
  ACTIONS_MENU_POPUP_CLASS,
  BulkAction,
  BulkActionType
} from '../activity-feed-constants';
import { downloadEvents } from '../activity-feed-download-utils';
import { useNotImplemented, useShowWipActions } from '../activity-feed-utils';
import { getBulkActions } from './activity-feed-list-utils';

const ACTION_DISPLAY: Record<BulkActionType, { label: string; icon: ReactElement }> = {
  [BulkAction.STAR]: { label: 'Star', icon: <MdOutlineGrade /> },
  [BulkAction.PIN]: { label: 'Pin for the team', icon: <MdOutlinePushPin /> },
  [BulkAction.SHARE]: { label: 'Share', icon: <MdIosShare /> },
  [BulkAction.HIDE]: { label: 'Hide', icon: <MdOutlineVisibilityOff /> },
  [BulkAction.MARK_TASKS_DONE]: { label: 'Mark tasks as done', icon: <MdCheck /> },
  [BulkAction.DOWNLOAD]: { label: 'Download', icon: <MdOutlineFileDownload /> },
  [BulkAction.DELETE]: { label: 'Delete', icon: <MdDeleteOutline /> }
};

const isEvent = (activity: AnyActivity): activity is Event => activity.kind === ActivityKind.EVENT;

interface BulkActionsMenuProps {
  checkedActivities: AnyActivity[];
}

export const BulkActionsMenu = ({ checkedActivities }: BulkActionsMenuProps): ReactNode => {
  const showWip = useShowWipActions();
  const notImplemented = useNotImplemented();
  const actions = getBulkActions({ showWip, checkedActivities });

  const options: MenuItem<BulkActionType>[] = actions.map(({ action, disabled }) => ({
    value: action,
    label: ACTION_DISPLAY[action].label,
    leadingContent: ACTION_DISPLAY[action].icon,
    disabled,
    className: action === BulkAction.DELETE ? ACTIONS_MENU_DESTRUCTIVE_CLASS : undefined
  }));

  const handleAction = (action: BulkActionType) => {
    if (action === BulkAction.DOWNLOAD) {
      downloadEvents(checkedActivities.filter(isEvent));
      return;
    }
    notImplemented();
  };

  return (
    <Dropdown
      type={DropdownToggleType.ICON_ONLY}
      overlayPlacement={Placement.BOTTOM}
      className={ACTIONS_MENU_CLASS}
      dropdownMenuClassName={ACTIONS_MENU_POPUP_CLASS}
      ariaLabel='Selected event actions'
      options={options}
      onChange={handleAction}
      leadingIcon={<RiMore2Fill />}
      placeholder=''
      stretch={StretchMode.FIT}
      menuAlign='start'
      disabled={checkedActivities.length === 0}
    />
  );
};
